import Konva from 'konva';
import { Circle } from 'konva/lib/shapes/Circle';
import { Line } from 'konva/lib/shapes/Line';
import { Path } from 'konva/lib/shapes/Path';
import { Rect } from 'konva/lib/shapes/Rect';
import { Subject, fromEvent, of, Observable, BehaviorSubject } from 'rxjs';
import { takeUntil, switchMap, pairwise } from 'rxjs/operators';

import { CdkDragRelease } from '@angular/cdk/drag-drop';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  OnInit,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { SnCurrentOrganization } from '@lib/auth';
import { t, TranslationKey } from '@lib/translation';

@Component({
  selector: 'sn-canvas',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
})
export class CanvasComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() set canvas(value: HTMLCanvasElement) {
    if (value && value !== this._canvas) {
      this._canvas = value;
      const scale = 800 / this._canvas.width;
      this._imageWidth = this._canvas.width * scale;
      this._imageHeight = this._canvas.height * scale;
      this._history.push(this._canvas.toDataURL('image/png', 1.0));
    }
  }

  get canvas(): HTMLCanvasElement {
    return this._canvas;
  }

  get konvaStage(): any {
    return this.stage;
  }

  get shapes$(): Observable<any[]> {
    return this._shapes$.asObservable();
  }

  get icons$(): Observable<any[]> {
    return this._icons$.asObservable();
  }

  @Output() public readonly canvasChange: EventEmitter<HTMLCanvasElement> = new EventEmitter();

  public stage: Konva.Stage;
  public layer: Konva.Layer;
  public activeColor: string = '#FF0000';
  public activeShape: string = 'Pen';
  public activeShapeFunction: string = 'canDrawNow';
  public lastShapeFunctionUsed: string = 'canDrawNow';
  public shapes: any[] = [
    {
      shapeName: 'Textbox',
      shapeIcon:
        'M432 72.7692V140.461C432 144.185 428.954 147.231 425.231 147.231C421.508 147.231 418.462 144.185 418.462 140.461V79.5385H262.769V431.538H330.462C334.203 431.538 337.231 434.566 337.231 438.307C337.231 442.049 334.185 445.077 330.462 445.077H181.538C177.815 445.077 174.769 442.03 174.769 438.307C174.769 434.584 177.797 431.538 181.538 431.538H249.231V79.5385H93.5385V140.461C93.5385 144.185 90.5092 147.231 86.7692 147.231C83.0292 147.231 80 144.185 80 140.461V72.7692C80 69.0292 83.0292 66 86.7692 66H425.231C428.954 66 432 69.0292 432 72.7692Z M512 0V512H0V0H512ZM496 16H16V496H496V16Z',
      functionName: 'createTextBox',
      type: 'basic',
    },
    {
      shapeName: 'Circle',
      shapeIcon:
        'M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256ZM256 16C123.5 16 16 123.5 16 256C16 388.5 123.5 496 256 496C388.5 496 496 388.5 496 256C496 123.5 388.5 16 256 16Z',
      functionName: 'createCircle',
      type: 'basic',
    },
    {
      shapeName: 'Triangle',
      shapeIcon: 'M493 493L259.907 19L19 493H493Z',
      functionName: 'createTriangle',
      type: 'basic',
    },
    {
      shapeName: 'Square',
      shapeIcon: 'M512 0V512H0V0H512ZM496 16H16V496H496V16Z',
      functionName: 'createSquare',
      type: 'basic',
    },
    {
      shapeName: 'Cross',
      shapeIcon:
        'M182.857 191.357H191.357V182.857V36.5714C191.357 21.074 203.995 8.5 219.429 8.5H292.571C308.005 8.5 320.643 21.074 320.643 36.5714V182.857V191.357H329.143H475.429C490.848 191.357 503.5 204.009 503.5 219.429V292.571C503.5 307.991 490.848 320.643 475.429 320.643H329.143H320.643V329.143V475.429C320.643 490.848 307.991 503.5 292.571 503.5H219.429C204.009 503.5 191.357 490.848 191.357 475.429V329.143V320.643H182.857H36.5714C21.1516 320.643 8.5 307.991 8.5 292.571V219.429C8.5 204.009 21.1516 191.357 36.5714 191.357H182.857Z',
      functionName: 'createCross',
      type: 'law',
    },
    {
      shapeName: 'Hazmat',
      shapeIcon:
        'M511.539 283.5C499.868 244.39 476.429 211.52 445.648 191.15C430.093 180.78 413.426 174.53 396.538 171.28C401.871 153.53 405.427 134.79 405.427 115.04C405.427 74.05 392.538 34.31 368.983 2.84C367.544 0.9473 365.677 0 363.633 0C362.421 0 361.191 0.3516 360.053 1.061C357.164 2.936 355.831 7.059 356.831 10.809C360.831 24.559 362.72 37.049 362.72 49.299C362.72 117.029 314.935 172.099 256.057 172.099C197.178 172.099 149.393 116.989 149.393 49.299C149.393 37.179 151.393 24.559 155.282 10.809C156.282 7.059 154.949 2.936 152.06 1.061C150.928 0.3516 149.684 0 148.529 0C146.484 0 144.529 0.9473 143.195 2.811C119.73 34.3 106.752 74.04 106.752 115C106.752 134.75 110.197 153.49 115.641 171.24C98.8413 174.5 82.0597 180.8 66.5046 191.1C35.7234 211.59 12.2753 244.34 0.391259 283.45C-0.719816 287.2 0.613473 291.198 3.50227 293.198C4.67734 293.981 5.95641 294.362 7.2097 294.362C9.15452 294.362 11.0398 293.446 12.3909 291.698C20.7257 280.828 29.2792 272.578 38.39 266.578C55.1361 255.438 73.4466 250.138 91.5349 250.138C128.405 250.138 164.315 272.158 184.065 311.568C213.513 370.178 195.958 444.668 145.062 479.368C135.951 485.491 125.507 489.738 113.17 492.738C109.946 493.613 107.501 496.988 107.501 500.861C107.613 504.861 109.946 508.109 113.281 508.859C122.663 510.1 132.174 512 141.507 512C167.506 512 193.096 504.228 215.7 489.18C231.699 478.68 244.925 464.69 255.921 449.19C266.925 464.69 280.258 478.68 296.257 489.18C318.834 504.2 344.433 512 370.388 512C379.792 512 389.232 510.982 398.591 508.924C402.036 508.174 404.258 504.926 404.369 500.926C404.369 497.053 402.036 493.678 398.702 492.803C386.365 489.93 375.921 485.555 366.926 479.433C315.923 445.563 298.475 370.233 327.816 311.633C347.575 272.233 383.485 250.203 420.346 250.203C438.443 250.203 456.781 255.499 473.562 266.643C482.557 272.766 491.117 280.893 499.455 291.763C500.806 293.511 502.734 294.427 504.685 294.427C505.943 294.427 507.213 294.046 508.345 293.263C511.45 291.2 512.783 287.2 511.539 283.5ZM480.073 252.8C461.683 240.59 440.777 234.14 419.622 234.14C376.397 234.14 336.185 260.8 314.647 303.72C281.493 369.96 301.359 454.92 358.797 493.12C360.167 494.052 361.562 494.948 362.983 495.809C341.695 494.604 320.993 487.61 302.647 475.359C289.332 466.621 277.092 454.459 266.257 439.209L255.146 423.559L244.036 439.209C233.147 454.549 220.943 466.719 207.681 475.419C189.353 487.619 168.634 494.609 147.381 495.819C148.782 494.981 150.153 494.112 151.494 493.212C209.86 454.9 229.681 369.9 196.438 303.7C174.928 260.8 134.751 234.2 90.7527 234.2C69.6067 234.2 48.7274 240.657 30.4258 252.83C29.4294 253.486 28.4383 254.166 27.4553 254.865C38.9926 234.375 54.5388 217.195 72.9737 204.925C86.0044 196.236 100.902 190.225 117.248 187.055L133.745 183.864L129.063 166C123.641 148.3 120.974 131.6 120.974 115C120.974 90.96 125.927 67.28 135.249 45.76C135.216 46.934 135.2 48.104 135.2 49.268C135.2 125.828 189.43 188.068 256.085 188.068C321.95 188.068 376.97 125.778 376.97 49.268C376.97 48.067 376.954 46.862 376.923 45.655C386.239 67.135 391.198 90.865 391.198 114.995C391.198 131.165 388.537 147.885 383.06 166.105L377.729 183.845L394.137 187.001C410.43 190.134 425.318 196.144 438.438 204.891C456.908 217.111 472.437 234.291 483.938 254.961C482.917 254.3 481.94 253.5 480.073 252.8ZM255.192 224.1C231.637 224.1 212.526 245.59 212.526 272.09C212.526 298.58 231.637 320.08 255.192 320.08C278.746 320.08 297.866 298.59 297.866 272.09C297.857 245.6 279.546 224.1 255.192 224.1ZM255.192 304.1C239.503 304.1 226.748 289.75 226.748 272.11C226.748 254.47 239.512 240.12 255.192 240.12C270.88 240.12 283.644 254.47 283.644 272.11C283.635 289.8 271.636 304.1 255.192 304.1ZM201.327 90.23C237.13 76.61 274.978 76.67 310.745 90.2925C311.495 90.5738 312.273 90.7144 313.023 90.7144C315.981 90.7144 318.745 88.6204 319.758 85.2764C321.022 81.1044 319.022 76.5574 315.3 75.1364C276.613 60.38 235.548 60.38 196.794 75.08C193.06 76.48 190.305 81.03 192.349 85.22C193.594 89.39 197.594 91.69 201.327 90.23ZM424.786 298.8C419.105 344.41 398.956 386.24 368.006 416.6C365.048 419.506 364.742 424.569 367.325 427.9C368.728 429.712 370.7 430.634 372.686 430.634C374.353 430.634 376.019 429.978 377.367 428.665C410.868 395.775 432.716 350.445 438.867 301.065C439.408 296.69 436.7 292.643 432.812 292.034C428.964 291.4 425.319 294.4 424.786 298.8ZM139.284 430.6C141.27 430.6 143.243 429.678 144.645 427.866C147.228 424.538 146.922 419.475 143.965 416.566C113.032 386.206 92.8819 344.376 87.2021 298.766C86.6465 294.375 82.9107 291.438 79.1748 291.954C75.2777 292.6 72.5666 296.6 73.1088 301C79.2615 350.41 101.09 395.72 134.591 428.6C135.907 429.1 137.596 430.6 139.284 430.6Z',
      functionName: 'createHazmat',
      type: 'law',
    },
    {
      shapeName: 'HouseClear',
      shapeIcon:
        'M251.349 2.01105C254.016 -0.67035 258.106 -0.67035 260.773 2.01105L509.721 250C512.656 252.9 512.922 257.1 509.544 261.3C507.765 264.6 503.231 264.9 500.297 261.1L455.22 217.1V448C455.22 483.3 429.702 512 398.317 512H113.805C82.3751 512 56.9024 483.3 56.9024 448V217.1L11.825 261.1C8.88826 264.9 4.39297 264.6 1.78879 261.3C-0.816454 257.1 -0.543856 252.9 2.39693 250L251.349 2.01105ZM71.128 202.9V448C71.128 474.5 90.2437 496 113.805 496H398.317C421.878 496 440.994 474.5 440.994 448V202.9L256.061 18.6901L71.128 202.9Z M441.88 52.1295L451.88 62.0684L20.9992 495.567L11 485.628L441.88 52.1295Z',
      functionName: 'createHouseClear',
      type: 'law',
    },
    {
      shapeName: 'HouseNotClear',
      shapeIcon:
        'M251.349 2.01105C254.016 -0.67035 258.106 -0.67035 260.773 2.01105L509.722 250C512.656 252.9 512.922 257.1 509.544 261.3C507.765 264.6 503.231 264.9 500.297 261.1L455.22 217.1V448C455.22 483.3 429.702 512 398.317 512H113.805C82.3751 512 56.9024 483.3 56.9024 448V217.1L11.825 261.1C8.88826 264.9 4.39297 264.6 1.78879 261.3C-0.816454 257.1 -0.543856 252.9 2.39694 250L251.349 2.01105ZM71.128 202.9V448C71.128 474.5 90.2437 496 113.805 496H398.317C421.878 496 440.994 474.5 440.994 448V202.9L256.061 18.6901L71.128 202.9Z',
      functionName: 'createHouseNotClear',
      type: 'law',
    },
    {
      shapeName: 'Phone',
      shapeIcon:
        'M493.1 351.209L384.6 304.709C371.82 299.178 356.82 302.903 348.13 313.684L304.05 367.484C234.8 333.484 178.55 277.184 144.55 207.984L198.38 163.924C209.1 155.109 212.8 140.209 207.3 127.509L160.8 18.9291C154.706 5.01911 139.67 -2.60089 124.98 0.809111L24.22 24.0591C9.969 27.3091 0 39.8591 0 54.5091C0 306.809 205.2 512.009 457.5 512.009C472.17 512.009 484.69 502.041 487.96 487.789L511.21 386.989C514.6 372.409 506.1 357.209 493.1 351.209ZM495.6 383.409L472.35 484.209C470.8 491.109 464.6 496.009 457.5 496.009C214 496.009 16 297.909 16 54.5091C16 47.3531 20.859 41.2591 27.81 39.6291L128.61 16.3791C135.813 14.7231 143.22 18.5041 146.19 25.3171L192.71 133.817C195.37 140.061 193.497 147.317 188.304 151.567L124.774 203.567C187.094 337.067 293.474 380.267 308.574 387.367L360.594 323.837C364.813 318.587 372.114 316.775 378.324 319.462L486.824 365.962C493.5 368.909 497.3 376.309 495.6 383.409Z',
      functionName: 'createPhone',
      type: 'law',
    },
    {
      shapeName: 'Weapon',
      shapeIcon:
        'M87.7242 291.109C91.0691 275.967 89.8574 260.112 84.3034 246.214H216.637L168.752 478.69C168.283 480.809 167.172 482.595 165.888 483.798C165.25 484.395 164.645 484.778 164.168 484.995C163.704 485.205 163.465 485.214 163.465 485.214L56.9067 485.214C56.038 485.214 54.412 484.743 52.8886 482.144L52.888 482.143C51.3811 479.573 50.7066 475.96 51.6346 471.763L51.6534 471.678L51.6704 471.593L87.7242 291.109ZM420.773 174.357H416.616L414.064 177.639L389.822 208.807C388.383 210.6 387.124 210.929 386.4 210.929H38.3733C31.6233 210.929 22.7222 202.912 22.7222 188.343V85.9429C22.7222 71.3646 31.6216 63.3572 38.3733 63.3572H483.556C484.325 63.3572 485.578 63.7401 486.898 65.4371C488.225 67.1424 489.278 69.842 489.278 73.1429V164.571C489.278 167.849 488.225 170.551 486.891 172.267C485.558 173.98 484.302 174.357 483.556 174.357H420.773Z',
      functionName: 'createWeapon',
      type: 'law',
    },
  ];

  public icons = [
    {
      name: 'BCAT',
      label: 'BearCat',
      type: 'law',
    },
    {
      name: 'MCAT',
      label: 'MedCat',
      type: 'law',
    },
    {
      name: 'CP',
      label: 'Command Post',
      type: 'law',
    },
    {
      name: 'EMS1',
      label: 'Medical 1',
      type: 'law',
    },
    {
      name: 'EMS2',
      label: 'Medical 2',
      type: 'law',
    },
    {
      name: 'EMS3',
      label: 'Medical 3',
      type: 'law',
    },
    {
      name: 'F1',
      label: 'Fire 1',
      type: 'law',
    },
    {
      name: 'F2',
      label: 'Fire 1',
      type: 'law',
    },
    {
      name: 'F3',
      label: 'Fire 3',
      type: 'law',
    },
    {
      name: 'S1',
      label: 'Sierra 1',
      type: 'law',
    },
    {
      name: 'S2',
      label: 'Sierra 2',
      type: 'law',
    },
    {
      name: 'S3',
      label: 'Sierra 3',
      type: 'law',
    },
    {
      name: 'T1',
      label: 'Team 1',
      type: 'basic',
    },
    {
      name: 'T2',
      label: 'Team 2',
      type: 'basic',
    },
    {
      name: 'T3',
      label: 'Team 3',
      type: 'basic',
    },
    {
      name: 'ET1',
      label: 'Entry Team 1',
      type: 'law',
    },
    {
      name: 'ET2',
      label: 'Entry Team 2',
      type: 'law',
    },
    {
      name: 'ET3',
      label: 'Entry Team 3',
      type: 'law',
    },
    {
      name: 'R1',
      label: 'React 1',
      type: 'law',
    },
    {
      name: 'R2',
      label: 'React 2',
      type: 'law',
    },
    {
      name: 'R3',
      label: 'React 3',
      type: 'law',
    },
    {
      name: 'ARR1',
      label: 'Arrest and Control 1',
      type: 'law',
    },
    {
      name: 'ARR2',
      label: 'Arrest and Control 2',
      type: 'law',
    },
    {
      name: 'ARR3',
      label: 'Arrest and Control 3',
      type: 'law',
    },
    {
      name: 'OA1',
      label: 'Outside Agency 1',
      type: 'law',
    },
    {
      name: 'OA2',
      label: 'Outside Agency 2',
      type: 'law',
    },
    {
      name: 'OA3',
      label: 'Outside Agency 3',
      type: 'law',
    },
    {
      name: '1',
      label: '1',
      type: 'basic',
    },
    {
      name: '2',
      label: '2',
      type: 'basic',
    },
    {
      name: '3',
      label: '3',
      type: 'basic',
    },
    {
      name: '4',
      label: '4',
      type: 'basic',
    },
    {
      name: '1/4',
      label: '1/4',
      type: 'basic',
    },
    {
      name: '1/2',
      label: '1/2',
      type: 'basic',
    },
    {
      name: '2/3',
      label: '2/3',
      type: 'basic',
    },
    {
      name: '3/4',
      label: '3/4',
      type: 'basic',
    },
    {
      name: 'K9',
      label: 'K9',
      type: 'law',
    },
    {
      name: 'HC1',
      label: 'Helicopter 1',
      type: 'law',
    },
    {
      name: 'HC2',
      label: 'Helicopter 2',
      type: 'law',
    },
    {
      name: 'MEDIA',
      label: 'Media',
      type: 'basic',
    },
  ];
  public lineMultiplier = 1;

  public textBoxes: Konva.Transformer[] = [];
  public squares: Konva.Transformer[] = [];
  public circles: Konva.Transformer[] = [];
  public triangles: Konva.Transformer[] = [];
  public crosses: Konva.Transformer[] = [];
  public weapons: Konva.Transformer[] = [];
  public hazmats: Konva.Transformer[] = [];
  public houseClears: Konva.Transformer[] = [];
  public houseNotClears: Konva.Transformer[] = [];
  public labels: Konva.Transformer[] = [];
  public phones: Konva.Transformer[] = [];

  public formControlCanvas = new UntypedFormControl(null);
  public form = new UntypedFormGroup({});

  public labelText: string = '';
  public simpleText: any[] = [];
  public canDraw: boolean = true;
  public chosenType = 'basic';
  private _shapes$: BehaviorSubject<any[]> = new BehaviorSubject(this.shapes);
  private _icons$: BehaviorSubject<any[]> = new BehaviorSubject(this.icons);

  private _lineScale = 4;
  private readonly _destroy$ = new Subject<void>();
  private _imageWidth: number;
  private _imageHeight: number;

  private _history: (Konva.Group | Konva.Transformer | Rect | Circle | Path | Line | string)[] = [];

  private _canvas: HTMLCanvasElement;
  private _cx: CanvasRenderingContext2D;

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    this.chosenType = this._currentOrganization.plan.isLawEnforcementPlan ? 'law' : 'basic';
  }

  public getTranslatedShapeName(shapeName: string): string {
    const shapeKey = shapeName.toLowerCase().replace(/\s+/g, '');
    const translationKey = `map.telestration.shapes.${shapeKey}`;
    return t(translationKey as TranslationKey) || shapeName;
  }

  public getTranslatedIconLabel(iconName: string): string {
    if (/^[\d/]+$/.test(iconName)) {
      return iconName;
    }

    const iconKey = iconName.toLowerCase().replace(/\s+/g, '');
    const translationKey = `map.icons.${iconKey}`;
    return t(translationKey as TranslationKey) || iconName;
  }

  public ngOnInit(): void {
    this.formControlCanvas.setValue('#FF0000');
    this.icons.sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public ngAfterViewInit(): void {
    const newCanvas = this._elementRef.nativeElement.querySelector('#canvasContainer canvas');
    newCanvas.height = this._imageHeight;
    const canvasEl: HTMLCanvasElement = newCanvas;
    const img = document.createElement('img');
    this._cx = canvasEl.getContext('2d');
    img.style.width = '600px';
    img.onload = () => {
      this._cx.drawImage(img, 0, 0, this._imageWidth, this._imageHeight);
    };
    img.src = this._canvas.toDataURL('image/png', 1.0);
    this.updateLineWidth();
    this._cx.lineCap = 'round';
    this._captureMouseEvents(canvasEl);
    this._captureTouchEvents(canvasEl);

    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }
  }

  public setType(type: string): void {
    this.chosenType = type;
    if (type === 'basic') {
      this._shapes$.next(this.shapes.filter((sh) => sh.type === type));
      this._icons$.next(this.icons.filter((ic) => ic.type === 'basic'));
    } else {
      this._shapes$.next(this.shapes);
      this._icons$.next(this.icons);
    }
  }

  public combineKonvaCanvas(): Observable<HTMLCanvasElement> {
    if (!this.stage) {
      return of(this.canvas);
    }
    this.clearTextboxOutline();
    this.clearSquareOutline();
    this.clearCircleOutline();
    this.clearTriangleOutline();
    this.clearCrossOutline();
    this.clearWeaponOutline();
    this.clearPhoneOutline();
    this.clearHouseClearOutline();
    this.clearHouseNotClearOutline();
    this.clearLabelOutline();
    const layer = new Konva.Layer({
      width: this.stage.width(),
      height: this.stage.height(),
    });
    this.stage.add(layer);
    const img = new Image();
    const onLoad$ = fromEvent(img, 'load');
    img.src = this.canvas.toDataURL('image/png', 1.0);
    return onLoad$.pipe(
      switchMap(() => {
        const canvasimage = new Konva.Image({
          x: 0,
          y: 0,
          image: img,
          width: this.stage.width(),
          height: this.stage.height(),
        });
        // add the shape to the layer
        layer.add(canvasimage);
        layer.moveToBottom();

        return of(this.stage.toCanvas());
      }),
    );
  }

  public toggleDraw(on: boolean): void {
    this.canDraw = on;
  }

  public setActiveColor(): void {
    if (this.formControlCanvas.value !== null) {
      this.formControlCanvas.setValue(this.formControlCanvas.value);
      this.activeColor = this.formControlCanvas.value;
    } else {
      this.formControlCanvas.setValue('#FF0000');
    }
  }

  public setShapeFunction(activeShape: string): void {
    this.activeShapeFunction = activeShape;
  }

  public dragRelease(shape: any, event: CdkDragRelease): void {
    const canvasContainer = document.querySelector('#canvasContainer');
    const element = event.source.element;
    const cbbox = canvasContainer.getBoundingClientRect();
    const bbox = element.nativeElement.getBoundingClientRect();
    const centerX = bbox.x - cbbox.x + bbox.width / 2;
    const centerY = bbox.y - cbbox.y + bbox.height / 2;
    this[shape.functionName](shape, centerX, centerY);
    event.source.reset();
    this.toggleDraw(false);
  }

  public textRelease(icon: any, event: CdkDragRelease): void {
    const canvasContainer = document.querySelector('#canvasContainer');
    const element = event.source.element;
    const cbbox = canvasContainer.getBoundingClientRect();
    const bbox = element.nativeElement.getBoundingClientRect();
    const centerX = bbox.x - cbbox.x;
    const centerY = bbox.y - cbbox.y;
    event.source.reset();
    this.toggleDraw(false);
    this.createTextImage(icon.name, centerX, centerY);
  }

  public updateLineWidth(): void {
    this._cx.lineWidth = this.lineMultiplier * this._lineScale;
  }

  public clearTextboxOutline(): void {
    this.textBoxes.forEach((box) => {
      box.hide();
    });
  }

  public clearSquareOutline(): void {
    this.squares.forEach((square) => {
      square.hide();
    });
  }

  public clearCircleOutline(): void {
    this.circles.forEach((circle) => {
      circle.hide();
    });
  }

  public clearTriangleOutline(): void {
    this.triangles.forEach((triangle) => {
      triangle.hide();
    });
  }

  public clearCrossOutline(): void {
    this.crosses.forEach((cross) => {
      cross.hide();
    });
  }

  public clearWeaponOutline(): void {
    this.weapons.forEach((weapon) => {
      weapon.hide();
    });
  }

  public clearHazmatOutline(): void {
    this.hazmats.forEach((hazmat) => {
      hazmat.hide();
    });
  }

  public clearPhoneOutline(): void {
    this.phones.forEach((phones) => {
      phones.hide();
    });
  }

  public clearHouseNotClearOutline(): void {
    this.houseNotClears.forEach((houseNotClear) => {
      houseNotClear.hide();
    });
  }

  public clearHouseClearOutline(): void {
    this.houseClears.forEach((houseClear) => {
      houseClear.hide();
    });
  }

  public clearLabelOutline(): void {
    this.labels.forEach((label) => {
      label.hide();
    });
  }

  public showTextboxOutline(): void {
    this.textBoxes.forEach((box) => {
      box.show();
    });
  }

  // Creates the textbox and puts it in the top left corner
  public createTextBox(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }
    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    // make this a variable, add to the simpleText array. array.push
    const newText = new Konva.Text({
      x: x - 10,
      y: y - 15,
      text: 'edit text',
      fontSize: 24,
      fill: this.activeColor,
      draggable: true,
      opacity: 1,
    });
    this.layer.add(newText);

    const tr = new Konva.Transformer({
      keepRatio: false,
    });
    this.textBoxes.push(tr);
    this._history.push(tr);
    this.textBoxes[this.textBoxes.length - 1].show();
    this.layer.add(this.textBoxes[this.textBoxes.length - 1]);
    this.textBoxes[this.textBoxes.length - 1].nodes([newText]);
    this.stage.add(this.layer);

    newText.on('dblclick dbltap', () => {
      newText.hide();

      // at first lets find position of text node relative to the stage:
      const textPosition = newText.absolutePosition();

      // so position of textarea will be the sum of positions above:
      const areaPosition = {
        x: Number(this.stage.container().offsetLeft) + Number(textPosition.x),
        y: Number(this.stage.container().offsetTop) + Number(textPosition.y),
      };

      // create textarea and style it
      const textarea = document.createElement('textarea');
      textarea.id = `textarea${this.textBoxes.length - 1}`;
      document.querySelector('.konvajs-content').appendChild(textarea);

      // apply many styles to match text on canvas as close as possible
      // remember that text rendering on canvas and on the textarea can be different
      // and sometimes it is hard to make it 100% the same. But we will try...
      textarea.value = newText.text();
      textarea.style.position = 'absolute';
      textarea.style.top = `${areaPosition.y}px`;
      textarea.style.left = `${areaPosition.x}px`;
      textarea.style.width = `${newText.width() - newText.padding() * 2}px`;
      textarea.style.height = `${newText.height() - newText.padding() * 2 + 5}px`;
      textarea.style.fontSize = `${newText.fontSize()}px`;
      textarea.style.border = 'none';
      textarea.style.padding = '0px';
      textarea.style.margin = '0px';
      textarea.style.overflow = 'hidden';
      textarea.style.background = 'none';
      textarea.style.outline = 'none';
      textarea.style.resize = 'none';
      textarea.style.lineHeight = newText.lineHeight().toString();
      textarea.style.transformOrigin = 'left top';
      textarea.style.textAlign = newText.align();
      textarea.style.color = newText.fill();
      const rotation = newText.rotation();
      let transform = '';
      if (rotation) {
        transform += `rotateZ(${rotation}deg)`;
      }

      let px = 0;
      // also we need to slightly move textarea on firefox
      // because it jumps a bit
      const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      if (isFirefox) {
        px += 2 + Math.round(newText.fontSize() / 20);
      }
      transform += `translateY(-${px}px)`;

      textarea.style.transform = transform;

      // reset height
      textarea.style.height = 'auto';
      // after browsers resized it we can set actual value
      textarea.style.height = `${textarea.scrollHeight + 3}px`;

      textarea.focus();

      function removeTextarea(): any {
        textarea.parentNode.removeChild(textarea);
        window.removeEventListener('click', handleOutsideClick);
        newText.show();
      }

      function deleteTextbox(): any {
        textarea.parentNode.removeChild(textarea);
        tr.remove();
        window.removeEventListener('click', handleOutsideClick);
        this.textboxes[this.simpleText.indexOf(newText)].remove();
        newText.remove();
        this.deleteIndex = null;
      }

      function setTextareaWidth(newWidth: any): any {
        if (!newWidth) {
          // set width for placeholder
          newWidth = newText.text.length * newText.fontSize();
        }
        // some extra fixes on different browsers
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isFirefox1 = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        if (isSafari || isFirefox1) {
          newWidth = Math.ceil(newWidth);
        }

        const isEdge = document['documentMode'] || /Edge/.test(navigator.userAgent);
        if (isEdge) {
          newWidth += 1;
        }
        textarea.style.width = `${newWidth}px`;
      }

      textarea.addEventListener('keydown', (e) => {
        // hide on enter
        // but don't hide on shift + enter
        if (e.keyCode === 13 && !e.shiftKey) {
          if (textarea.value === '') {
            deleteTextbox();
          }
          this.textBoxes.forEach((box) => {
            box.show();
          });
          newText.text(textarea.value);
          removeTextarea();
        }
        // on esc do not set value back to node
        if (e.keyCode === 27) {
          removeTextarea();
        }
      });

      textarea.addEventListener('keydown', (e) => {
        const scale = newText.getAbsoluteScale().x;
        setTextareaWidth(newText.width() * scale);
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight + Number(newText.fontSize())}px`;
      });

      function handleOutsideClick(e: any): void {
        if (e.target !== textarea) {
          this.deleteIndex = null;
          if (textarea.value === '') {
            deleteTextbox();
          }
          if (this.textBoxes) {
            this.textBoxes?.forEach((box) => {
              box.show();
            });
          }
          newText.text(textarea.value);
          removeTextarea();
        }
      }
      setTimeout(() => {
        window.addEventListener('click', handleOutsideClick);
      });
      this.simpleText[this.simpleText.length - 1] = newText;
    });
  }

  public createSquare(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newSquare = new Konva.Rect({
      x,
      y,
      width: 36,
      height: 36,
      fill: 'transparent',
      draggable: true,
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      strokeScaleEnabled: false,
    });

    this.layer.add(newSquare);
    this.stage.add(this.layer);
    this._history.push(newSquare);

    // Function to edit/rotate any squares on the board
    newSquare.addEventListener('click', (e) => {
      const squareTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newSquare.setDraggable(true);
      this.squares.push(squareTr);
      this.layer.add(squareTr);
      squareTr.nodes([newSquare]);
    });

    // Double Click to disable movement and remove ability to rotate/resize/etc.
    newSquare.addEventListener('dblclick', (e) => {
      newSquare.setDraggable(false);
      this.clearSquareOutline();
    });

    newSquare.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newSquare.destroy();
      }
    });
  }

  public createCircle(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newCircle = new Konva.Circle({
      x,
      y,
      radius: 18,
      fill: 'transparent',
      draggable: true,
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      strokeScaleEnabled: false,
    });
    this.layer.add(newCircle);
    this.stage.add(this.layer);
    this._history.push(newCircle);

    newCircle.addEventListener('click', (e) => {
      const circleTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newCircle.setDraggable(true);
      this.circles.push(circleTr);
      this.layer.add(circleTr);
      circleTr.nodes([newCircle]);
    });

    newCircle.addEventListener('dblclick', (e) => {
      newCircle.setDraggable(false);
      this.clearCircleOutline();
    });

    newCircle.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newCircle.destroy();
      }
    });
  }

  public createTriangle(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }
    const newTriangle = new Konva.Line({
      points: [x, y - 18, x - 18, y + 18, x + 18, y + 18],
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      scale: {
        x: 1,
        y: 1,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newTriangle);
    this.stage.add(this.layer);
    this._history.push(newTriangle);

    newTriangle.addEventListener('click', (e) => {
      const triangleTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newTriangle.setDraggable(true);
      this.triangles.push(triangleTr);
      this.layer.add(triangleTr);
      triangleTr.nodes([newTriangle]);
    });

    newTriangle.addEventListener('dblclick', (e) => {
      newTriangle.setDraggable(false);
      this.clearTriangleOutline();
    });

    newTriangle.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newTriangle.destroy();
      }
    });
  }

  public createCross(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newCross = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newCross);
    this.stage.add(this.layer);
    this._history.push(newCross);

    newCross.addEventListener('click', (e) => {
      const crossTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newCross.setDraggable(true);
      this.crosses.push(crossTr);
      this.layer.add(crossTr);
      crossTr.nodes([newCross]);
    });

    newCross.addEventListener('dblclick', (e) => {
      newCross.setDraggable(false);
      this.clearCrossOutline();
    });

    newCross.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newCross.destroy();
      }
    });
  }

  public createWeapon(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newWeapon = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newWeapon);
    this.stage.add(this.layer);
    this._history.push(newWeapon);

    newWeapon.addEventListener('click', (e) => {
      const weaponTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newWeapon.setDraggable(true);
      this.weapons.push(weaponTr);
      this.layer.add(weaponTr);
      weaponTr.nodes([newWeapon]);
    });

    newWeapon.addEventListener('dblclick', (e) => {
      newWeapon.setDraggable(false);
      this.clearWeaponOutline();
    });

    newWeapon.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newWeapon.destroy();
      }
    });
  }

  public createHazmat(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newHazmat = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth * 0.5,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newHazmat);
    this.stage.add(this.layer);
    this._history.push(newHazmat);

    newHazmat.addEventListener('click', (e) => {
      const hazmatTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newHazmat.setDraggable(true);
      this.hazmats.push(hazmatTr);
      this.layer.add(hazmatTr);
      hazmatTr.nodes([newHazmat]);
    });

    newHazmat.addEventListener('dblclick', (e) => {
      newHazmat.setDraggable(false);
      this.clearHazmatOutline();
    });

    newHazmat.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newHazmat.destroy();
      }
    });
  }

  public createPhone(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newPhone = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth * 0.5,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newPhone);
    this.stage.add(this.layer);
    this._history.push(newPhone);

    newPhone.addEventListener('click', (e) => {
      const phoneTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newPhone.setDraggable(true);
      this.phones.push(phoneTr);
      this.layer.add(phoneTr);
      phoneTr.nodes([newPhone]);
    });

    newPhone.addEventListener('dblclick', (e) => {
      newPhone.setDraggable(false);
      this.clearPhoneOutline();
    });

    newPhone.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newPhone.destroy();
      }
    });
  }

  public createTextImage(name: string, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const group = new Konva.Group({
      x,
      y,
      draggable: true,
    });

    const text = new Konva.Text({
      x: 2,
      y: 3,
      text: name,
      fontSize: 18,
      fill: 'white',
      align: 'center',
      stroke: 'black',
      strokeWidth: 3,
      fillAfterStrokeEnabled: true,
    });

    const rectBlack = new Konva.Rect({
      width: +text.width() + 4,
      height: +text.height() + 2,
      fill: 'transparent',
      stroke: 'black',
      strokeWidth: 5,
    });

    const rectWhite = new Konva.Rect({
      width: +text.width() + 4,
      height: +text.height() + 2,
      fill: 'transparent',
      stroke: 'white',
      strokeWidth: 2,
    });

    group.add(text);
    group.add(rectBlack);
    group.add(rectWhite);
    this.layer.add(group);
    this._history.push(group);
    this.layer.draw();
    this.stage.add(this.layer);
  }

  public createHouseClear(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newHouseClear = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: '#0000FF',
      strokeWidth: this._cx.lineWidth,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newHouseClear);
    this.stage.add(this.layer);
    this._history.push(newHouseClear);

    newHouseClear.addEventListener('click', (e) => {
      const houseClearTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newHouseClear.setDraggable(true);
      this.houseClears.push(houseClearTr);
      this.layer.add(houseClearTr);
      houseClearTr.nodes([newHouseClear]);
    });

    newHouseClear.addEventListener('dblclick', (e) => {
      newHouseClear.setDraggable(false);
      this.clearHouseClearOutline();
    });

    newHouseClear.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newHouseClear.destroy();
      }
    });
  }

  public createHouseNotClear(shape: any, x: number, y: number): void {
    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: 'konvaContainer',
        width: this._imageWidth,
        height: this._imageHeight,
      });
    }

    if (!this.layer) {
      this.layer = new Konva.Layer({
        width: this.stage.width(),
        height: this.stage.height(),
      });
    }

    const newHouseNotClear = new Konva.Path({
      x: x - 18,
      y: y - 18,
      data: shape.shapeIcon,
      fill: 'transparent',
      stroke: this.activeColor,
      strokeWidth: this._cx.lineWidth,
      scale: {
        x: 0.075,
        y: 0.075,
      },
      draggable: true,
      closed: true,
      strokeScaleEnabled: false,
    });

    this.layer.add(newHouseNotClear);
    this.stage.add(this.layer);
    this._history.push(newHouseNotClear);

    newHouseNotClear.addEventListener('click', (e) => {
      const houseNotClearTr = new Konva.Transformer({
        anchorStroke: 'white',
        anchorFill: 'transparent',
        anchorSize: 7,
        borderDash: [3, 3],
        keepRatio: true,
      });
      newHouseNotClear.setDraggable(true);
      this.houseNotClears.push(houseNotClearTr);
      this.layer.add(houseNotClearTr);
      houseNotClearTr.nodes([newHouseNotClear]);
    });

    newHouseNotClear.addEventListener('dblclick', (e) => {
      newHouseNotClear.setDraggable(false);
      this.clearHouseNotClearOutline();
    });

    newHouseNotClear.addEventListener('click', (e: any) => {
      if (e.detail === 3) {
        newHouseNotClear.destroy();
      }
    });
  }

  public undo(): void {
    const img = document.createElement('img');
    img.onload = () => {
      this._cx.clearRect(0, 0, this._imageWidth, this._imageHeight);
      this._cx.drawImage(img, 0, 0, this._imageWidth, this._imageHeight);
    };

    if (this._history.length > 1) {
      const mostRecentItem = this._history.pop();

      if (typeof mostRecentItem === 'object') {
        mostRecentItem.destroy();
      } else {
        img.src = this._history.length === 1 ? this._history[0].toString() : mostRecentItem;
      }
    }
  }

  public clear(): void {
    const img = document.createElement('img');
    img.onload = () => {
      this._cx.clearRect(0, 0, this._imageWidth, this._imageHeight);
      this._cx.drawImage(img, 0, 0, this._imageWidth, this._imageHeight);
    };
    if (this.layer !== Konva.Layer[0]) {
      this.layer.destroyChildren();
    }
    this.textBoxes = [];
    this._history = [this._history[0]];
    img.src = this._history.toString();
  }

  private _captureMouseEvents(canvasEl: HTMLCanvasElement): void {
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e) => {
          return fromEvent(canvasEl, 'mousemove').pipe(
            pairwise(),
            takeUntil(fromEvent(canvasEl, 'mouseup')),
            takeUntil(fromEvent(canvasEl, 'mouseleave')),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top,
        };

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top,
        };

        this._drawOnCanvas(prevPos, currentPos);
      });

    fromEvent(canvasEl, 'mouseup')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._history.push(canvasEl.toDataURL('image/png'));
      });
  }

  private _captureTouchEvents(canvasEl: HTMLCanvasElement): void {
    fromEvent(canvasEl, 'touchstart')
      .pipe(
        switchMap((e) => {
          e.preventDefault();
          return fromEvent(canvasEl, 'touchmove').pipe(
            pairwise(),
            takeUntil(fromEvent(canvasEl, 'touchend')),
            takeUntil(fromEvent(canvasEl, 'mouseleave')),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((res: [TouchEvent, TouchEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        const prevPos = {
          x: res[0].touches[0].clientX - rect.left,
          y: res[0].touches[0].clientY - rect.top,
        };

        const currentPos = {
          x: res[1].touches[0].clientX - rect.left,
          y: res[1].touches[0].clientY - rect.top,
        };

        this._drawOnCanvas(prevPos, currentPos);
      });

    fromEvent(canvasEl, 'touchend')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._history.push(canvasEl.toDataURL('image/png'));
      });
  }

  private _drawOnCanvas(
    prevPos: { x: number; y: number },
    currentPos: { x: number; y: number },
  ): void {
    this._cx.strokeStyle = this.activeColor;

    if (!this._cx) {
      return;
    }

    this._cx.beginPath();

    if (prevPos) {
      this._cx.moveTo(prevPos.x, prevPos.y); // from
      this._cx.lineTo(currentPos.x, currentPos.y);
      this._cx.stroke();
      this.canvasChange.next(this._cx.canvas);
    }
  }
}
